import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../utils/api';

type AdvisorySettings = {
  tenant_id: number;
  advisory_service: 'local' | 'remote';
  remote_base_url: string | null;
  api_key_set: boolean;
  remote_key_prefix: string | null;
  fallback_to_local: boolean;
  timeout_ms: number;
  last_test_at: string | null;
  last_test_result: string | null;
};

export default function AdvisorySettingsPanel({ tenantId }: { tenantId: number }) {
  const [settings, setSettings] = useState<AdvisorySettings | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null);
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestSequence.current;
    if (!Number.isInteger(tenantId) || tenantId <= 0) {
      setSettings(null);
      setMessage({ error: true, text: 'Select a valid workspace to manage advisory settings.' });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await apiGet<AdvisorySettings>(`/api/integrations/advisory-settings/${tenantId}`);
      if (requestId !== requestSequence.current) return;
      setSettings(result);
      setMessage(null);
    } catch (error) {
      if (requestId !== requestSequence.current) return;
      setMessage({ error: true, text: error instanceof Error ? error.message : 'Could not load advisory settings.' });
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!settings) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload: Record<string, unknown> = {
        advisory_service: settings.advisory_service,
        remote_base_url: settings.remote_base_url || null,
        fallback_to_local: settings.fallback_to_local,
        timeout_ms: settings.timeout_ms,
      };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const updated = await apiPut<AdvisorySettings>(
        `/api/integrations/advisory-settings/${tenantId}`,
        payload,
      );
      setSettings(updated);
      setApiKey('');
      setMessage({ error: false, text: 'Advisory settings saved. The API key is stored encrypted and will not be shown again.' });
    } catch (error) {
      setMessage({ error: true, text: error instanceof Error ? error.message : 'Could not save advisory settings.' });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setMessage(null);
    try {
      const result = await apiPost<{ ok: boolean; message: string }>(
        `/api/integrations/advisory-settings/${tenantId}/test`,
        {},
      );
      setMessage({ error: !result.ok, text: result.message });
      await load();
    } catch (error) {
      setMessage({ error: true, text: error instanceof Error ? error.message : 'Connection test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const clearKey = async () => {
    if (!settings || !window.confirm('Remove the stored remote advisory key? Remote mode will stop until a new key is saved.')) return;
    setSaving(true);
    try {
      const updated = await apiPut<AdvisorySettings>(
        `/api/integrations/advisory-settings/${tenantId}`,
        { ...settings, api_key: null },
      );
      setSettings(updated);
      setMessage({ error: false, text: 'Remote key removed.' });
    } catch (error) {
      setMessage({ error: true, text: error instanceof Error ? error.message : 'Could not remove the key.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading assistant settings…</div>;
  if (!settings) return <div className="p-8 text-sm text-rose-700">{message?.text || 'Settings unavailable.'}</div>;

  return (
    <form onSubmit={save} className="space-y-6 p-6 sm:p-8">
      <header>
        <h2 className="text-lg font-bold text-slate-900">Field assistant</h2>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">Choose eSanza local advice or Umufasha Remote for this workspace. Remote answers use a short-lived session; the partner key stays on the backend.</p>
      </header>
      {message && <div role="status" className={`rounded-md border px-4 py-3 text-sm ${message.error ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{message.text}</div>}

      <section className="space-y-3">
        <label htmlFor="advisory-service" className="block text-sm font-semibold text-slate-800">Advice service</label>
        <select id="advisory-service" value={settings.advisory_service} onChange={(e) => setSettings({ ...settings, advisory_service: e.target.value as 'local' | 'remote' })} className="w-full max-w-xl rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20">
          <option value="local">eSanza Local</option>
          <option value="remote">Umufasha Remote</option>
        </select>
        <label className="flex max-w-xl items-start gap-3 rounded-md border border-slate-200 p-3 text-sm text-slate-700">
          <input type="checkbox" checked={settings.fallback_to_local} onChange={(e) => setSettings({ ...settings, fallback_to_local: e.target.checked })} className="mt-0.5 accent-emerald-700" />
          <span><span className="font-semibold">Use local advice if Remote text chat is unavailable</span><span className="mt-1 block text-xs text-slate-500">Voice and photo requests will ask the user to retry when Remote is unavailable.</span></span>
        </label>
      </section>

      <section className="grid max-w-3xl gap-4 border-t border-slate-200 pt-5">
        <div>
          <label htmlFor="advisory-url" className="mb-1.5 block text-sm font-semibold text-slate-800">Remote service URL</label>
          <input id="advisory-url" type="url" value={settings.remote_base_url || ''} onChange={(e) => setSettings({ ...settings, remote_base_url: e.target.value })} placeholder="https://…" autoComplete="url" className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20" />
        </div>
        <div>
          <label htmlFor="advisory-key" className="mb-1.5 block text-sm font-semibold text-slate-800">Partner API key</label>
          <input id="advisory-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={settings.api_key_set ? 'Saved key is hidden; leave empty to keep it' : 'oak_…'} autoComplete="new-password" className="w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700/20" />
          <p className="mt-1.5 text-xs text-slate-500">{settings.api_key_set ? `A key is saved (${settings.remote_key_prefix || 'prefix hidden'}). It is write-only.` : 'No key is currently saved.'}</p>
        </div>
      </section>

      <footer className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-5">
        <button type="submit" disabled={saving} className="inline-flex items-center rounded-md bg-emerald-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">{saving ? 'Saving…' : 'Save settings'}</button>
        <button type="button" onClick={test} disabled={testing || saving || !settings.api_key_set} className="rounded-md border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">{testing ? 'Testing…' : 'Test connection'}</button>
        {settings.api_key_set && <button type="button" onClick={clearKey} disabled={saving} className="ml-auto rounded-md px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50">Remove key</button>}
      </footer>
      {settings.last_test_at && <p className="text-xs text-slate-500">Last connection test: {new Date(settings.last_test_at).toLocaleString()} · {settings.last_test_result}</p>}
    </form>
  );
}
